import { useRealtimeStore } from '@/stores/realtime-store'
import { useStatesStore } from '@/stores/states-store'
import { getCurrentInstance } from 'vue'
import { processAmounts } from '@/helpers/results-helper'
import en from '@/locales/en'

export const useRealtime = (props) => {
  const realtimeStore = useRealtimeStore()
  const statesStore = useStatesStore()
  const instance = getCurrentInstance()
  const $tr = instance?.proxy?.$tr || ((messages, key) => key)

  // Filter function to remove freetext actions if disabled in org features
  const removeFreetextFilterFunction = (organizationFeatures) => (a) =>
    !organizationFeatures.pulsAI?.disableFreetext || a.type !== 'freetext'

  // Calculate excludedTags from props
  const getExcludedTags = () => {
    return [
      ...(props.surveyDetails?.resultViews?.groupComparison?.excludedTags ?? [])
    ]
  }

  // Find action details in category (moved here for use in getGroupActions)
  const fullAction = (fullCategory, actionName) => {
    const foundAction = (fullCategory.feedbacks || []).reduce(
      (feedbacksRes, f) => {
        const actionsFound = f.details.actions.filter(
          (a) => a.name === actionName
        )

        if (actionsFound.length) {
          feedbacksRes = actionsFound[0]
        }
        return feedbacksRes
      },
      null
    )
    return foundAction
  }

  // Calculate group actions with titles
  const getGroupActions = () => {
    const organizationFeatures = statesStore.organizationFeatures
    return (props.actions || [])
      .filter(removeFreetextFilterFunction(organizationFeatures))
      .map((a) => {
        const actionTitle = (props.surveyDetails?.categories || []).reduce(
          (res, c) => {
            const actionWithDetails = fullAction(c, a.actionName)
            if (actionWithDetails) {
              const title = $tr(actionWithDetails.messages, 'title')
              res = title
            }
            return res
          },
          null
        )
        return { ...a, actionTitle }
      })
  }

  // Extract data block from message text ({{...}} format)
  const extractDataBlock = (str) => {
    const start = str.indexOf('{{')
    if (start === -1) return null

    let i = start + 2
    // Skip any whitespace after '{{'
    while (i < str.length && /\s/.test(str[i])) i++

    // Parse JSON/braces until the object closes
    let braceCount = 0
    let inString = false
    let escaping = false
    const startJson = i

    for (; i < str.length; i++) {
      const ch = str[i]
      if (inString) {
        if (escaping) {
          escaping = false
        } else if (ch === '\\') {
          escaping = true
        } else if (ch === '"') {
          inString = false
        }
      } else {
        if (ch === '"') {
          inString = true
        } else if (ch === '{' || ch === '[') {
          braceCount++
        } else if (ch === '}' || ch === ']') {
          braceCount--
          if (braceCount === 0) {
            i++ // move past the closing brace of the JSON object
            break
          }
        }
      }
    }

    const endJson = i

    // Find the first closing brace of the delimiter
    let j = i
    while (j < str.length && /\s/.test(str[j])) j++
    if (j >= str.length || str[j] !== '}') return null

    // Find the second closing brace of the delimiter
    let k = j + 1
    while (k < str.length && /\s/.test(str[k])) k++
    if (k >= str.length || str[k] !== '}') return null

    const dataString = str.slice(startJson, endJson).trim()
    return { dataString, start, end: k } // `end` now points to the second }
  }

  // Format timestamp to HH:MM format
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  // Calculate seconds waiting for response
  const secondsWaitingForResponse = () => {
    if (!realtimeStore.waitingForResponseSince) {
      return 0
    }
    const now = new Date()
    const diff = now - realtimeStore.waitingForResponseSince
    return Math.floor(diff / 1000)
  }

  // Extract data from message and parse embedded JSON
  const extractDataFromMessage = (message) => {
    let text = message.text ?? ''
    let data = {}

    const block = extractDataBlock(text)
    if (block) {
      try {
        data = JSON.parse(block.dataString)
      } catch {
        try {
          // Some messages might omit the outer {}
          data = JSON.parse(`{${block.dataString}}`)
        } catch {
          data = {}
        }
      }
      // Remove everything from '{{' up to and including the final '}'
      text = (text.slice(0, block.start) + text.slice(block.end + 1)).trim()
    }

    return { text, data }
  }

  return {
    // realtime store state
    get messages() {
      return realtimeStore.messages
    },
    get error() {
      return realtimeStore.error
    },
    get loading() {
      return realtimeStore.loading
    },
    get waitingForResponse() {
      return realtimeStore.waitingForResponse
    },
    get waitingForResponseSince() {
      return realtimeStore.waitingForResponseSince
    },

    // state store state

    get organizationFeatures() {
      return statesStore.organizationFeatures
    },
    get surveyIsAggregate() {
      return statesStore.surveyIsAggregate
    },

    // computed properties
    get dataInResponse() {
      // Get the last message from the messages array and extract its data
      const modelsFound = Object.keys(realtimeStore.messages)
      if (!modelsFound.length) return {}

      const messages =
        realtimeStore.messages[modelsFound[modelsFound.length - 1]]
      if (!messages || messages.length === 0) return {}

      const lastMessage = messages[messages.length - 1] || {}

      // If the message already has extracted data, use it
      if (lastMessage.data) {
        return lastMessage.data
      }

      // Otherwise, extract the data from the message text
      const { data } = this.extractDataFromMessage(lastMessage)
      return data
    },

    get proposalsFromAI() {
      return this.dataInResponse.followUpProposals || []
    },

    suggestionTexts(t) {
      const keys = Object.keys(
        en.resultsmessages.suggestions[props.section] || {}
      )
      if (keys.length === 0) return []

      // Get the first random index and key
      const firstIndex = Math.floor(Math.random() * keys.length)
      const firstKey = keys[firstIndex]

      let secondIndex
      do {
        // Get a second random index until it's different from the first one
        secondIndex = Math.floor(Math.random() * keys.length)
      } while (secondIndex === firstIndex && keys.length > 1)

      const secondKey = keys[secondIndex]

      const groupName = props.selectedGroup?.messages?.en?.title || 'all'

      return [
        t(`resultsmessages.suggestions.${props.section}.${firstKey}`, {
          groupName
        }),
        t(`resultsmessages.suggestions.${props.section}.${secondKey}`, {
          groupName
        })
      ]
    },

    get notEnoughResults() {
      if (props.section === 'groupsComparison') {
        return false
      }
      const surveyData = this.surveyData
      const noQuestionAnswers = (surveyData.questions || []).every((q) => {
        return q.value === 0 || !Object.keys(q).includes('value')
      })
      return (
        (surveyData && surveyData.questions.length === 0) || noQuestionAnswers
      )
    },

    // other getters

    get surveyData() {
      const excludedTags = getExcludedTags()
      const statItemsWithTitleAppended = ({
        type,
        statItems = {},
        trendItems = {},
        selectedAttributeSegment = {}
      }) => {
        return Object.keys(statItems)
          .filter((itemName) => !excludedTags.includes(itemName))
          .filter(
            (itemName) =>
              itemName !== 'likely_would_recommend_employer' &&
              itemName !== 'puls_enps'
          )
          .map((itemName) => {
            const statItem = statItems[itemName]
            const i = {
              value: statItem.value,
              sum: statItem.sum,
              numberOfResponses: statItem.numberOfResponses,
              numberOfQuestions: statItem.numberOfQuestions,
              name: itemName,
              title: $tr(statItem.messages, 'title')
            }
            if (statItem.npsData) {
              i.npsData = statItem.npsData
            }

            // fetch averages from trendItems or single construct item if there is no trend
            let averages
            const hasHistoricalSurveys =
              trendItems[i.name]?.averages?.length > 0
            if (!hasHistoricalSurveys) {
              const answersForpropsSurvey = []
              processAmounts(
                answersForpropsSurvey,
                statItem,
                (target, i, amount) => {
                  target.push({ value: i, amount })
                }
              )

              const currentSurveyAverage = {
                date_modified: statItem.date_modified,
                surveyName: props.surveyName,
                value: statItem.value,
                amount: statItem.amount,
                sum: statItem.sum,
                answers: answersForpropsSurvey
              }

              if (statItem.npsData) {
                currentSurveyAverage.npsData = statItem.npsData
              }
              averages = [currentSurveyAverage]
            } else {
              averages = trendItems[i.name].averages
            }
            i.averages = averages.map((a, index) => {
              const newAverage = {
                answers: a.answers,
                sum: a.sum,
                value: a.value,
                surveyName: a.surveyName,
                date: a.date_modified,
                timestamp: a.date_modified,
                numberOfResponses: a.amount,
                linearRegressionValue:
                  trendItems[i.name]?.regressionValues[index]
              }
              if (a.npsData) {
                newAverage.npsData = a.npsData
              }

              // if (a.surveyName === props.surveyName) {
              //   newAverage.sum = i.sum
              //   newAverage.answers = answersForpropsSurvey
              // }
              return newAverage
            })

            const additionalItemStats = {}
            if (type === 'tags') {
              if (i.name === 'enps') {
                // we only keep enps score
                ;(i.averages || []).forEach((average) => {
                  delete average.value
                })
              }
              if (i.name === 'participation') {
                if (i.title === '-') {
                  // some templates do not have translation for participation
                  i.title = 'Participation'
                }

                if (selectedAttributeSegment.name !== 'all') {
                  // for segments we don't know number of invited people
                  i.value = 'unknown'
                  i.numberOfResponses = i.sum / 10
                  ;(i.averages || []).forEach((average) => {
                    i.linearRegressionValue = 'unknown'
                    average.answers[0] = { value: 0, amount: 0 }
                    average.value = 'unknown'
                    average.linearRegressionValue = 'unknown'
                    average.numberOfResponses = average.sum / 10
                  })
                }
              }
              // TODO: maybe clean up participation tag that requires less data
            } else if (type === 'categories') {
            } else if (type === 'questions') {
              const c = props.surveyDetails.categories.find((c) =>
                c.questions.some((q) => q.name === i.name)
              )

              if (c) {
                const question = c.questions.find((q) => q.name === i.name)

                if (c) {
                  const question = c.questions.find((q) => q.name === i.name)

                  // const questionMessages = question?.details?.messages || {}
                  if (question.details.yesIsBad) {
                    additionalItemStats.yesIsBad = question.details.yesIsBad
                  }
                  additionalItemStats.type = question.details.type
                  additionalItemStats.tags = question.tags
                  additionalItemStats.categoryName = c.name
                }
                additionalItemStats.type = question.details.type
                additionalItemStats.tags = question.tags
                additionalItemStats.categoryName = c.name
              }
            }

            return {
              ...i,
              ...additionalItemStats
            }
          })
      }
      if (props.section === 'groupsComparison') {
        return {
          organizationFullName: props.organizationFullName,
          surveyTitle: $tr(props.surveyDetails.messages, 'title'),
          surveyName: props.surveyName,
          section: props.section
        }
      } else {
        const data = {
          selectedGroup: {
            name: props.selectedGroup.name,
            title: `${$tr(props.selectedGroup.messages, 'title')}`
          },
          organizationName: props.organizationName,
          organizationFullName: props.organizationFullName,
          section: 'overview',
          surveyName: props.surveyName,
          surveyTitle: $tr(props.surveyDetails.messages, 'title'),
          tags: statItemsWithTitleAppended({
            type: 'tags',
            statItems: props.tags,
            trendItems: props.trendTags,
            selectedAttributeSegment: props.selectedAttributeSegment
          }),
          categories: statItemsWithTitleAppended({
            type: 'categories',
            statItems: props.categories,
            trendItems: props.trendCategories
          }),
          questions: statItemsWithTitleAppended({
            type: 'questions',
            statItems: props.questions,
            trendItems: props.trendQuestions
          })
        }

        // Add group actions with titles calculated from composable
        const groupActions = getGroupActions()
        if (groupActions?.length) {
          data.actions = groupActions
        }

        if (props.selectedAttributeSegment.name !== 'all') {
          data.selectedAttributeSegment = props.selectedAttributeSegment
        }

        return data
      }
    },
    // utility methods (non-UI)
    formatTimestamp,
    secondsWaitingForResponse,
    extractDataFromMessage,
    fullAction,

    // actions
    setSection: ({ section }) => realtimeStore.setSection({ section }),
    clear: () => {
      return realtimeStore.clear()
    },
    clearAllStoredMessages: () => realtimeStore.clearAllStoredMessages(),
    getStoredMessages: () => {
      return realtimeStore.getStoredMessages()
    },
    // unused?!
    findStoredMessagesByTag: (tag) =>
      realtimeStore.findStoredMessagesByTag(tag),
    loadStoredMessagesFromSession: ({ sessionId, section }) =>
      realtimeStore.loadStoredMessagesFromSession({
        sessionId,
        section
      }),
    sendRealtimeMessageAction: ({
      message,
      surveyName,
      groupName,
      aggregate,
      section,
      surveyData
    }) =>
      realtimeStore.sendRealtimeMessageAction({
        message,
        surveyName,
        groupName,
        aggregate,
        section,
        surveyData
      }),
    // Widget-specific actions - these don't change the active session
    getMessagesFromSpecificSession: (sessionId) =>
      realtimeStore.getMessagesFromSpecificSession(sessionId),
    updateStoredMessagesForSpecificSession: ({ sessionId, messages }) =>
      realtimeStore.updateStoredMessagesForSpecificSession({
        sessionId,
        messages
      }),
    sendRealtimeMessageActionWithSessionId: ({
      sessionId,
      message,
      surveyName,
      groupName,
      aggregate,
      surveyData
    }) =>
      realtimeStore.sendRealtimeMessageActionWithSessionId({
        sessionId,
        message,
        surveyName,
        groupName,
        aggregate,
        surveyData
      })
  }
}
